import { create } from 'zustand';

/**
 * Global store for CalendarPanel state persistence across route changes.
 * When CalendarPanel unmounts (user navigates away), state is retained here.
 * When CalendarPanel remounts (user navigates back), it pulls from this store.
 */
const useCalendarStore = create((set) => ({
    // View state
    view: 'week',
    currentDate: new Date(),
    sidebarOpen: true,
    mobileSidebarOpen: false,
    darkMode: localStorage.getItem('theme') === 'dark' || (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches),

    // Data
    events: [],
    calendars: [],
    visibleCalendars: new Set(['main', 'algorithm']),
    faculty: [],
    batches: [],
    subjects: [],
    branches: [],
    assignments: [],
    settings: { working_hours_start: '08:00', working_hours_end: '18:00' },
    templates: [],
    templateRefreshTrigger: 0,

    // Flags
    hasAlgoEvents: false,

    // Search
    searchOpen: false,
    searchType: 'faculty',
    searchEntityId: '',
    searchFaculty: '',
    searchBatch: '',
    searchRoom: '',
    highlights: [],

    // Availability
    showAvailFor: new Set(),

    // Painter
    painterMode: null,
    painterForm: { entity_type: 'faculty', entity_id: '', availability_mode: 'unavailable' },

    // Actions
    setView: (view) => set({ view }),
    setCurrentDate: (updater) => set((state) => ({
        currentDate: typeof updater === 'function' ? updater(state.currentDate) : updater,
    })),
    setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
    setMobileSidebarOpen: (mobileSidebarOpen) => set({ mobileSidebarOpen }),
    setDarkMode: (darkMode) => {
        localStorage.setItem('theme', darkMode ? 'dark' : 'light');
        set({ darkMode });
    },
    setEvents: (updater) => set((state) => ({
        events: typeof updater === 'function' ? updater(state.events) : updater,
    })),
    setCalendars: (calendars) => set({ calendars }),
    setVisibleCalendars: (updater) => set((state) => ({
        visibleCalendars: typeof updater === 'function' ? updater(state.visibleCalendars) : updater,
    })),
    setFaculty: (faculty) => set({ faculty }),
    setBatches: (batches) => set({ batches }),
    setSubjects: (subjects) => set({ subjects }),
    setBranches: (branches) => set({ branches }),
    setAssignments: (assignments) => set({ assignments }),
    setTemplates: (templates) => set({ templates }),
    triggerTemplateRefresh: () => set((state) => ({ templateRefreshTrigger: state.templateRefreshTrigger + 1 })),
    setSettings: (settings) => set((state) => {
        const newWeights = { ...state.schedulerWeights };
        // If settings are 0-100, scale down. If 0-10, use directly.
        const scale = (val) => (val > 15 ? Math.round(val / 10) : val);

        if (settings.scheduler_morning_weight != null)
            newWeights.morning_preference = scale(settings.scheduler_morning_weight);
        if (settings.scheduler_preference_weight != null)
            newWeights.faculty_preference = scale(settings.scheduler_preference_weight);
        if (settings.scheduler_compactness_weight != null)
            newWeights.compactness = scale(settings.scheduler_compactness_weight);
        if (settings.scheduler_activation_weight != null)
            newWeights.fulfillment_priority = scale(settings.scheduler_activation_weight);

        return {
            settings: { ...state.settings, ...settings },
            schedulerWeights: newWeights,
        };
    }),
    setHasAlgoEvents: (hasAlgoEvents) => set({ hasAlgoEvents }),
    setSearchOpen: (searchOpen) => set({ searchOpen }),
    setSearchType: (searchType) => set({ searchType, searchEntityId: '' }),
    setSearchEntityId: (searchEntityId) => set({ searchEntityId }),
    setSearchFaculty: (searchFaculty) => set({ searchFaculty }),
    setSearchBatch: (searchBatch) => set({ searchBatch }),
    setSearchRoom: (searchRoom) => set({ searchRoom }),
    setHighlights: (highlights) => set({ highlights }),
    setShowAvailFor: (updater) => set((state) => ({
        showAvailFor: typeof updater === 'function' ? updater(state.showAvailFor) : updater,
    })),
    setPainterMode: (painterMode) => set({ painterMode }),
    setPainterForm: (updater) => set((state) => ({
        painterForm: typeof updater === 'function' ? updater(state.painterForm) : updater,
    })),

    // ─── Generative Template Studio Staging ────────────────
    stagedTemplates: {}, // Format: { [dateStr]: { templateId, session_id, payload: {...} } }
    setStagedTemplate: (dateStr, data) => set((state) => ({
        stagedTemplates: { ...state.stagedTemplates, [dateStr]: data }
    })),
    removeStagedTemplate: (dateStr) => set((state) => {
        const newStaged = { ...state.stagedTemplates };
        delete newStaged[dateStr];
        return { stagedTemplates: newStaged };
    }),
    clearStagedTemplates: () => set({ stagedTemplates: {} }),
    draggedTemplate: null,
    setDraggedTemplate: (draggedTemplate) => set({ draggedTemplate }),

    // ─── Scheduler Sidebar ─────────────────────────────────
    schedulerSidebarOpen: false,
    schedulerSessionId: null,
    schedulerDate: null,
    schedulerProcessing: false,
    schedulerWeights: {
        morning_preference: 3,
        faculty_preference: 5,
        compactness: 2,
        fulfillment_priority: 10,
    },
    schedulerSessions: [],
    schedulerResults: null,

    setSchedulerSidebarOpen: (open) => set({ schedulerSidebarOpen: open }),
    setSchedulerSessionId: (id) => set({ schedulerSessionId: id }),
    setSchedulerDate: (date) => set({ schedulerDate: date }),
    setSchedulerProcessing: (v) => set({ schedulerProcessing: v }),
    setSchedulerWeights: (updater) => set((state) => ({
        schedulerWeights: typeof updater === 'function' ? updater(state.schedulerWeights) : updater,
    })),
    setSchedulerSessions: (sessions) => set({ schedulerSessions: sessions }),
    setSchedulerResults: (results) => set({ schedulerResults: results }),
    clearSchedulerState: () => set({
        schedulerSidebarOpen: false,
        schedulerSessionId: null,
        schedulerDate: null,
        schedulerProcessing: false,
        schedulerResults: null,
    }),

    // ─── Scheduling Job Tracker ────────────────────────────
    schedulingJobs: [],          // list of job objects from /api/scheduling/jobs
    activeJobId: null,           // currently running job_id (or null)
    setSchedulingJobs: (jobs) => set({ schedulingJobs: jobs }),
    setActiveJobId: (id) => set({ activeJobId: id }),
    updateSchedulingJob: (job) => set((state) => {
        const existing = state.schedulingJobs.find(j => j.id === job.id);
        if (existing) {
            return { schedulingJobs: state.schedulingJobs.map(j => j.id === job.id ? { ...j, ...job } : j) };
        }
        return { schedulingJobs: [job, ...state.schedulingJobs] };
    }),

    // ─── Students Panel Persisted Filters ───────────────────────
    studentsPanelFilterCourse: localStorage.getItem('sp_filterCourse') || '',
    studentsPanelFilterBatch: localStorage.getItem('sp_filterBatch') || '',
    studentsPanelSortBy: localStorage.getItem('sp_sortBy') || 'name-asc',

    setStudentsPanelFilterCourse: (val) => {
        localStorage.setItem('sp_filterCourse', val);
        set({ studentsPanelFilterCourse: val });
    },
    setStudentsPanelFilterBatch: (val) => {
        localStorage.setItem('sp_filterBatch', val);
        set({ studentsPanelFilterBatch: val });
    },
    setStudentsPanelSortBy: (val) => {
        localStorage.setItem('sp_sortBy', val);
        set({ studentsPanelSortBy: val });
    },

    // ─── CalendarSync Portal State ─────────────────────────────
    scholarsanchorTheme: 'lavender',
    scholarsanchorHistoryOpen: true,
    scholarsanchorBoardOpen: false,
    scholarsanchorKnowledgeOpen: false,
    scholarsanchorActiveConversationId: null,
    scholarsanchorActiveModel: 'llama-3.3-70b-versatile',
    scholarsanchorSelectedUserId: null,
    scholarsanchorTargetConversationId: null,
    scholarsanchorIsAdminView: false,
    scholarsanchorSidebarCollapsed: false,
    scholarsanchorActiveTab: 'overview',

    // CalendarSync Data Cache
    scholarsanchorConversations: [],
    scholarsanchorNotes: [],
    scholarsanchorPersonalities: [],
    scholarsanchorStudents: [],
    scholarsanchorOpenedFiles: [],
    scholarsanchorActiveFileId: null,

    setScholarsAnchorTheme: (theme) => set({ scholarsanchorTheme: theme }),
    setScholarsAnchorHistoryOpen: (open) => set((state) => ({ 
        scholarsanchorHistoryOpen: open,
        scholarsanchorBoardOpen: open ? false : state.scholarsanchorBoardOpen,
        scholarsanchorKnowledgeOpen: open ? false : state.scholarsanchorKnowledgeOpen
    })),
    setScholarsAnchorBoardOpen: (open) => set((state) => ({ 
        scholarsanchorBoardOpen: open,
        scholarsanchorHistoryOpen: open ? false : state.scholarsanchorHistoryOpen,
        scholarsanchorKnowledgeOpen: open ? false : state.scholarsanchorKnowledgeOpen
    })),
    setScholarsAnchorKnowledgeOpen: (open) => set((state) => ({ 
        scholarsanchorKnowledgeOpen: open,
        scholarsanchorBoardOpen: open ? false : state.scholarsanchorBoardOpen,
        scholarsanchorHistoryOpen: open ? false : state.scholarsanchorHistoryOpen
    })),
    setScholarsAnchorActiveConversationId: (id) => set({ scholarsanchorActiveConversationId: id }),
    setScholarsAnchorActiveModel: (model) => set({ scholarsanchorActiveModel: model }),
    setScholarsAnchorSelectedUserId: (id) => set({ scholarsanchorSelectedUserId: id }),
    setScholarsAnchorTargetConversationId: (id) => set({ scholarsanchorTargetConversationId: id }),
    setScholarsAnchorIsAdminView: (isAdmin) => set({ scholarsanchorIsAdminView: isAdmin }),
    setScholarsAnchorSidebarCollapsed: (collapsed) => set({ scholarsanchorSidebarCollapsed: collapsed }),
    setScholarsAnchorActiveTab: (tab) => set({ scholarsanchorActiveTab: tab }),

    setScholarsAnchorConversations: (conversations) => set({ scholarsanchorConversations: conversations }),
    setScholarsAnchorNotes: (notes) => set({ scholarsanchorNotes: notes }),
    setScholarsAnchorPersonalities: (personalities) => set({ scholarsanchorPersonalities: personalities }),
    setScholarsAnchorStudents: (students) => set({ scholarsanchorStudents: students }),
    setScholarsAnchorOpenedFiles: (updater) => set((state) => ({
        scholarsanchorOpenedFiles: typeof updater === 'function' ? updater(state.scholarsanchorOpenedFiles) : updater
    })),
    setScholarsAnchorActiveFileId: (id) => set({ scholarsanchorActiveFileId: id }),
}));

export default useCalendarStore;
